import { setTokenGetter, useApiInstance } from "@/providers/api-provider";
import { UserProfile } from "oidc-client-ts";
import { create } from "zustand";

import createSelectors from "./create-selectors";

interface IstariPermission {
  id: string;
  name: string;
  default: boolean;
  roles: string[];
}

export interface IstariUserProfile extends UserProfile {
  defaultOrg?: {
    id: string;
    name: string;
  };
  organizations?: string[];
  permissions?: IstariPermission[];
  superAdmin?: boolean;
  roleKeys?: string[];
}

export interface ListUser {
  id: string;
  state: string;
  human: {
    email: {
      email: string;
      isEmailVerified: boolean;
    };
    profile: {
      displayName: string;
      firstName: string;
      lastName: string;
    };
  };
  email: string;
  name: string;
  roleKeys: string[];
  grantId: string;
  organization_id?: string;
  organization_name?: string;
  home_organization_id?: string;
  organizations?: { id: string; name: string }[];
  orgRoles?: Record<string, string[]>;
  orgGrantIds?: Record<string, string>;
}

type UserStoreState = {
  user: IstariUserProfile;
  setUser: (user: IstariUserProfile) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  token: string;
  setToken: (token: string) => void;
  orgUsers: ListUser[];
  fetchOrgUsers: () => Promise<void>;
};

const useUserStoreBase = create<UserStoreState>()((set) => ({
  user: {} as IstariUserProfile,
  setUser: (user) =>
    set((state) => {
      const prev = state.user;
      const isSameUser = !prev.sub || prev.sub === user.sub;
      const merged = isSameUser
        ? {
            ...user,
            organizations: user.organizations ?? prev.organizations,
            permissions: user.permissions ?? prev.permissions,
            defaultOrg: user.defaultOrg ?? prev.defaultOrg,
            superAdmin: user.superAdmin ?? prev.superAdmin,
          }
        : { ...user };
      const roleKeySet = new Set<string>();
      if (merged.permissions) {
        merged.permissions.forEach((perm) => {
          perm.roles?.forEach((role) => roleKeySet.add(role));
        });
      }
      return { ...state, user: { ...merged, roleKeys: Array.from(roleKeySet) } };
    }),
  isAuthenticated: false,
  setIsAuthenticated: (isAuthenticated) => set((state) => ({ ...state, isAuthenticated })),
  token: "",
  setToken: (token) =>
    set((state) => {
      setTokenGetter(() => useUserStoreBase.getState().token);
      return { ...state, token: token };
    }),
  orgUsers: [],
  fetchOrgUsers: async (): Promise<void> => {
    const user = useUserStoreBase.getState().user;
    if (user?.defaultOrg?.id) {
      const response: { data: unknown } = await useApiInstance.get(
        `/api/v1/organizations/${user?.defaultOrg?.id}/users/list`,
      );
      console.info("Fetched org users:", response.data);
      set((state) => {
        return {
          ...state,
          orgUsers: prepareOrgUsers(response.data as ListUser[]),
        };
      });
      return response.data as void;
    }
  },
}));

const prepareOrgUsers = (orgUsers: ListUser[]) => {
  return orgUsers.map((user) => ({
    ...user,
    name: user.human.profile.displayName || "No Display Name",
    email: user.human.email.email || "No Email",
    state: (() => {
      let state = user.state;
      if (state.startsWith("USER_STATE_")) {
        state = state.replace("USER_STATE_", "");
      } else {
        state = "unknown";
      }
      return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
    })(),
  }));
};

export const useUserStore = createSelectors(useUserStoreBase);
